/**
 * YearlyHeatmap - GitHub-style contribution graph for the entire year
 */

import { useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  format,
  getMonth,
  getDay,
  isToday,
  isFuture,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { useMoodMapStore, MOODS } from '../../stores/moodmapStore';

const MONTHS = [
  'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz',
  'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'
];

const WEEKDAYS = ['Pzt', '', 'Çar', '', 'Cum', '', ''];

const YearlyHeatmap = () => {
  const {
    currentYear,
    yearlyEntries,
    loadYearlyEntries,
    selectDate,
  } = useMoodMapStore();

  // Load yearly entries on mount and year change
  useEffect(() => {
    loadYearlyEntries();
  }, [currentYear, loadYearlyEntries]);

  // Generate all days of the year
  const yearDays = useMemo(() => {
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(new Date(currentYear, 0, 1));
    return eachDayOfInterval({ start: yearStart, end: yearEnd });
  }, [currentYear]);

  // Create a map of entries by date
  const entriesByDate = useMemo(() => {
    const map = {};
    yearlyEntries.forEach((entry) => {
      map[entry.date] = entry;
    });
    return map;
  }, [yearlyEntries]);

  // Group days by week for the grid
  const weeks = useMemo(() => {
    const weeksArray = [];
    let currentWeek = [];
    let firstDayOffset = getDay(yearDays[0]);
    // Adjust for Monday start (getDay returns 0 for Sunday)
    firstDayOffset = firstDayOffset === 0 ? 6 : firstDayOffset - 1;

    // Add empty cells for days before the first day of year
    for (let i = 0; i < firstDayOffset; i++) {
      currentWeek.push(null);
    }

    yearDays.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeksArray.push(currentWeek);
        currentWeek = [];
      }
    });

    // Add remaining days
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeksArray.push(currentWeek);
    }

    return weeksArray;
  }, [yearDays]);

  // Get month labels with their positions
  const monthLabels = useMemo(() => {
    const labels = [];
    let currentMonth = -1;

    weeks.forEach((week, weekIndex) => {
      const firstDay = week.find((d) => d !== null);
      if (firstDay) {
        const month = getMonth(firstDay);
        if (month !== currentMonth) {
          labels.push({ month, weekIndex });
          currentMonth = month;
        }
      }
    });

    return labels;
  }, [weeks]);

  // Get mood color for a day
  const getMoodColor = (mood) => {
    if (!mood) return 'bg-gray-800';
    const colors = {
      great: 'bg-emerald-500',
      good: 'bg-green-500',
      neutral: 'bg-yellow-500',
      bad: 'bg-orange-500',
      awful: 'bg-red-500',
    };
    return colors[mood] || 'bg-gray-800';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 overflow-x-auto"
    >
      {/* Month labels */}
      <div className="flex mb-2 ml-8">
        {monthLabels.map(({ month, weekIndex }) => (
          <div
            key={month}
            className="text-xs text-gray-400"
            style={{
              marginLeft: weekIndex === 0 ? 0 : `${(weekIndex - (monthLabels[monthLabels.indexOf({ month, weekIndex }) - 1]?.weekIndex || 0)) * 14 - 20}px`,
              width: '40px',
            }}
          >
            {MONTHS[month]}
          </div>
        ))}
      </div>

      <div className="flex">
        {/* Weekday labels */}
        <div className="flex flex-col gap-[2px] mr-2 text-xs text-gray-400">
          {WEEKDAYS.map((day, i) => (
            <div key={i} className="h-3 flex items-center">
              {day}
            </div>
          ))}
        </div>

        {/* Heatmap grid */}
        <div className="flex gap-[2px]">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-[2px]">
              {week.map((day, dayIndex) => {
                if (!day) {
                  return (
                    <div
                      key={`empty-${dayIndex}`}
                      className="w-3 h-3"
                    />
                  );
                }

                const dateStr = format(day, 'yyyy-MM-dd');
                const entry = entriesByDate[dateStr];
                const isTodayDate = isToday(day);
                const isFutureDate = isFuture(day);

                return (
                  <motion.button
                    key={dateStr}
                    whileHover={{ scale: 1.3 }}
                    onClick={() => !isFutureDate && selectDate(dateStr)}
                    disabled={isFutureDate}
                    className={`
                      w-3 h-3 rounded-sm transition-colors
                      ${getMoodColor(entry?.mood)}
                      ${isTodayDate ? 'ring-1 ring-purple-400' : ''}
                      ${isFutureDate ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:ring-1 hover:ring-white/50'}
                      ${!entry?.mood && !isFutureDate ? 'hover:bg-gray-700' : ''}
                    `}
                    title={`${format(day, 'd MMMM yyyy', { locale: tr })}${entry?.mood ? ` - ${MOODS[entry.mood]?.label}` : ''}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-4 mt-4 pt-4 border-t border-gray-800">
        <span className="text-xs text-gray-400">Daha az</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-gray-800" />
          {Object.entries(MOODS).reverse().map(([mood, config]) => (
            <div
              key={mood}
              className={`w-3 h-3 rounded-sm ${getMoodColor(mood)}`}
              title={config.label}
            />
          ))}
        </div>
        <span className="text-xs text-gray-400">Daha fazla</span>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-center gap-6 mt-4 text-sm">
        <div className="text-gray-400">
          <span className="text-white font-medium">{yearlyEntries.length}</span> gün kayıt
        </div>
        {Object.entries(MOODS).map(([mood, config]) => {
          const count = yearlyEntries.filter((e) => e.mood === mood).length;
          if (count === 0) return null;
          return (
            <div key={mood} className="flex items-center gap-1.5">
              <span>{config.emoji}</span>
              <span className="text-gray-400">{count}</span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default YearlyHeatmap;
